import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { Form, Input, message, Modal } from 'antd';
import { isAxiosError } from 'axios';

import { useDescriptionValidation } from '@/shared/hooks';
import { MODAL_WIDTHS, ROUTES, VALIDATION_MESSAGES, VALIDATION_RULES } from '@/shared/lib/constants';
import { highlightText } from '@/shared/lib/text/highlight';
import { CategoryTree } from '@/shared/ui/CategoryTree';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';
import { SearchInput } from '@/shared/ui/SearchInput';

import { useProjectsExperiments } from '@/entities/experiment';
import { useCreateProject } from '@/entities/project';

import styles from './ExperimentTree.module.css';

type ExperimentTreeProps = {
  selectedProjectId?: string;
  selectedExperimentId?: string;
};

type TreeNode = {
  key: string;
  title: React.ReactNode;
  count?: number;
  isLeaf?: boolean;
  children?: TreeNode[];
};

export const ExperimentTree = ({ selectedProjectId, selectedExperimentId }: ExperimentTreeProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevSelectedProjectIdRef = useRef<string | undefined>(selectedProjectId);
  const [form] = Form.useForm();
  const { descriptionError, handleValuesChange, validateBeforeSubmit, resetError } = useDescriptionValidation({
    errorMessage: '프로젝트 생성 실패',
  });

  const { data: treeResponse } = useProjectsExperiments({ query: searchQuery.trim() || undefined });
  const createProjectMutation = useCreateProject();

  const allTreeData = useMemo(() => {
    if (!treeResponse?.projects) return [];

    return treeResponse.projects.map((project) => {
      const hasExperiments = project.experiments.length > 0;

      return {
        key: String(project.id),
        title: project.name,
        count: undefined,
        isLeaf: !hasExperiments,
        children: hasExperiments
          ? project.experiments.map((exp) => ({
              key: `${project.id}/${exp.id}`,
              title: exp.name,
              count: exp.runCount,
              isLeaf: true,
            }))
          : undefined,
      };
    });
  }, [treeResponse]);

  const treeData = useMemo(() => {
    if (!searchQuery.trim()) {
      return allTreeData;
    }

    const query = searchQuery.toLowerCase();
    const filtered: TreeNode[] = [];

    allTreeData.forEach((project) => {
      const projectTitle = project.title?.toString() ?? '';
      const projectMatches = projectTitle.toLowerCase().includes(query);
      const matchedExperiments = (project.children || []).filter((exp) => {
        const expTitle = exp.title?.toString() ?? '';
        return expTitle.toLowerCase().includes(query);
      });

      if (projectMatches || matchedExperiments.length > 0) {
        const children = projectMatches
          ? project.children?.map((exp) => {
              const expTitle = exp.title?.toString() ?? '';
              return {
                ...exp,
                title: highlightText(expTitle, searchQuery, { className: 'highlight' }),
              };
            })
          : matchedExperiments.map((exp) => {
              const expTitle = exp.title?.toString() ?? '';
              return {
                ...exp,
                title: highlightText(expTitle, searchQuery, { className: 'highlight' }),
              };
            });

        const hasChildren = children && children.length > 0;

        filtered.push({
          ...project,
          title: highlightText(projectTitle, searchQuery, { className: 'highlight' }),
          isLeaf: !hasChildren,
          children: hasChildren ? children : undefined,
        });
      }
    });

    return filtered;
  }, [allTreeData, searchQuery]);

  const selectedKeys = useMemo(() => {
    if (selectedExperimentId && selectedProjectId) {
      return [`${selectedProjectId}/${selectedExperimentId}`];
    }
    if (selectedProjectId) {
      return [selectedProjectId];
    }
    return [];
  }, [selectedProjectId, selectedExperimentId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchedProjectKeys: string[] = [];

      allTreeData.forEach((project) => {
        const hasMatchedExperiment = (project.children || []).some((exp) => {
          const expTitle = exp.title?.toString() ?? '';
          return expTitle.toLowerCase().includes(query);
        });
        if (hasMatchedExperiment) {
          matchedProjectKeys.push(project.key);
        }
      });

      setExpandedKeys(matchedProjectKeys);
    }
  }, [searchQuery, allTreeData]);

  useEffect(() => {
    if (!isInitialized && !selectedProjectId && allTreeData.length > 0) {
      const firstProjectId = allTreeData[0].key;
      const size = searchParams.get('size');
      const url = size
        ? `${ROUTES.EXPERIMENTS}/${firstProjectId}?size=${size}`
        : `${ROUTES.EXPERIMENTS}/${firstProjectId}`;
      navigate(url, { replace: true });
      setExpandedKeys([firstProjectId]);
      setIsInitialized(true);
      prevSelectedProjectIdRef.current = firstProjectId;
      return;
    }

    if (!isInitialized && selectedProjectId) {
      setIsInitialized(true);
      setExpandedKeys((prev) => Array.from(new Set([...prev, selectedProjectId])));
      prevSelectedProjectIdRef.current = selectedProjectId;
      return;
    }

    if (selectedProjectId && selectedProjectId !== prevSelectedProjectIdRef.current) {
      setExpandedKeys((prev) => Array.from(new Set([...prev, selectedProjectId])));
      prevSelectedProjectIdRef.current = selectedProjectId;
    }
  }, [selectedProjectId, navigate, isInitialized, allTreeData, searchParams]);

  const handleSelect = useCallback(
    (keys: React.Key[]) => {
      if (keys.length === 0) return;
      const key = keys[0] as string;
      const size = searchParams.get('size');
      const url = size ? `${ROUTES.EXPERIMENTS}/${key}?size=${size}` : `${ROUTES.EXPERIMENTS}/${key}`;
      navigate(url);
    },
    [navigate, searchParams]
  );

  const handleExpand = useCallback((newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
  }, []);

  const handleCreateProject = async () => {
    try {
      const values = await form.validateFields();

      if (!validateBeforeSubmit(values.description)) {
        return;
      }

      try {
        const newProject = await createProjectMutation.mutateAsync({
          name: values.name,
          description: values.description,
        });
        setIsModalOpen(false);
        resetError();
        form.resetFields();
        const size = searchParams.get('size');
        const url = size
          ? `${ROUTES.EXPERIMENTS}/${newProject.id}?size=${size}`
          : `${ROUTES.EXPERIMENTS}/${newProject.id}`;
        navigate(url);
      } catch (error: unknown) {
        message.error('프로젝트 생성 실패');
        if (isAxiosError(error) && error.response?.data?.message) {
          const errorMessage = error.response.data.message;
          if (errorMessage.includes('already exists') || errorMessage.includes('이미 존재')) {
            form.setFields([
              {
                name: 'name',
                errors: ['이미 존재하는 이름입니다. 다른 이름을 입력해 주세요'],
              },
            ]);
          }
        }
      }
    } catch {
      // Validation error already shown by validateBeforeSubmit
    }
  };

  const handleExpandAll = useCallback(() => {
    const allProjectKeys = allTreeData.map((project) => project.key);
    setExpandedKeys(allProjectKeys);
  }, [allTreeData]);

  return (
    <div className={styles.container}>
      <CategoryTree
        headerTitle="실험 프로젝트"
        showAddButton={true}
        addButtonText="프로젝트 생성"
        onAddClick={() => setIsModalOpen(true)}
        searchSlot={
          <SearchInput
            placeholder="프로젝트 또는 실험 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        }
        treeData={treeData}
        selectedKeys={selectedKeys}
        expandedKeys={expandedKeys}
        onSelect={handleSelect}
        onExpand={handleExpand}
        onCollapseAll={() => setExpandedKeys([])}
        onExpandAll={handleExpandAll}
        loadingKeys={[]}
        isLoading={false}
        showCount={true}
        searchQuery={searchQuery.trim()}
      />

      <Modal
        title="새 프로젝트 생성"
        open={isModalOpen}
        onOk={handleCreateProject}
        onCancel={() => {
          setIsModalOpen(false);
          resetError();
          form.resetFields();
        }}
        okText="생성"
        cancelText="취소"
        confirmLoading={createProjectMutation.isPending}
        destroyOnHidden
        width={MODAL_WIDTHS.COMPACT_FORM}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: '', description: '' }}
          onValuesChange={handleValuesChange}>
          <Form.Item
            name="name"
            label="프로젝트명"
            rules={[
              { required: true, message: VALIDATION_MESSAGES.PROJECT_NAME_REQUIRED },
              { max: VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH, message: VALIDATION_MESSAGES.PROJECT_NAME_MAX_LENGTH },
            ]}
            validateFirst>
            <Input
              placeholder="프로젝트명을 입력하세요"
              className={styles.projectNameInput}
              maxLength={VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="설명" required>
            <DescriptionTextArea
              placeholder="프로젝트에 대한 설명을 입력하세요"
              showHelper
              minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
              maxLength={VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
              status={descriptionError ? 'error' : undefined}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
